import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { TrendingUp } from 'lucide-react'
import { Button, NameStrategyDialog } from '@/components'
import { botApi, type Bot } from './api'

/**
 * Title-only strategy creation: a "Name your strategy" popup over a blank
 * page. The bot is only created once the user confirms the name; no prompt is
 * sent to the AI — the user lands on the Workbench tab and talks to the AI
 * later. The old prompt-first flow remains available at /strategy/new.
 */
export default function BotCreate() {
  const [open, setOpen] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Set by the sidebar's "New Strategy" button — its presence means this
  // entry was pushed from inside the app, so history-back is safe. Without
  // it (post-sign-in redirect, deep link) "back" could leave the app or land
  // on the sign-in page, so dismiss falls back to the user's first strategy.
  const from = (location.state as { from?: string } | null)?.from
  const botsPromise = useRef<Promise<Bot[]> | null>(null)

  useEffect(() => {
    if (from) return
    botsPromise.current = botApi.list().catch((err) => {
      console.error('Error loading bots:', err)
      return []
    })
  }, [from])

  const handleDismiss = async () => {
    if (from) {
      navigate(-1)
      return
    }
    const bots = await (botsPromise.current ?? Promise.resolve([]))
    if (bots?.length) {
      navigate(`/strategy/${bots[0].bid}`, { replace: true })
    } else {
      setOpen(false)
    }
  }

  const handleSubmit = async (title: string) => {
    setIsSubmitting(true)
    setError(null)

    try {
      const { bid } = await botApi.createEmpty(title)
      navigate(`/strategy/${bid}?tab=workbench`, { replace: true })
    } catch (err) {
      console.error('Error creating strategy:', err)
      setError('Failed to create strategy. Please try again.')
      setIsSubmitting(false)
    }
  }

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-background">
      {!open && (
        <Button className="gap-2" onClick={() => setOpen(true)}>
          <TrendingUp size={14} strokeWidth={1.75} />
          Create a strategy
        </Button>
      )}
      <NameStrategyDialog
        open={open}
        onOpenChange={(next) => {
          if (!next && !isSubmitting) handleDismiss()
        }}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        error={error}
      />
    </div>
  )
}
